import "./calendar.css";
import "react-toastify/dist/ReactToastify.css";
import React, { useCallback, useEffect, useState } from "react";
import { DateToggleControl, Modal } from "../../../components";
import { useLocation } from "react-router-dom";
import { useAppContext } from "../../../context/AppContext";
import {
	CreateEvent,
	ViewEvent,
	EditEvent,
	SuccessModal,
} from "../../../components/Modal";
import { ToastContainer } from "react-toastify";
import { videoCamera } from "../../../assets";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";

function Calendar() {
	const location = useLocation();
	const { filteredEventData } = useAppContext();
	const [view, setView] = useState("Calendar");

	const [showModal, setShowModal] = useState(false);
	const [modalContent, setModalContent] = useState(null);

	const [EVENTS, setEVENTS] = useState([]);
	const [allEvents, setAllEvents] = useState([]);

	const [currentEvents, setCurrentEvents] = useState([]);

	// eslint-disable-next-line no-unused-vars
	const [calendarDate, setCalendarDate] = useState([]);

	const [currentTimeZoneOffset, setCurrentTimeZoneOffset] = useState("");
	const [currentTimeZone, setCurrentTimeZone] = useState("");

	// Get Time Zone Info:
	const getTimeZoneInfo = () => {
		const timeZoneAbbr = new Date()
			.toLocaleTimeString("en-us", { timeZoneName: "short" })
			.split(" ")[2];
		const offset = new Date()
			.toLocaleTimeString("en-us", { timeZoneName: "long" })
			.split(" ")[4];

		setCurrentTimeZoneOffset(offset);
		setCurrentTimeZone(timeZoneAbbr);
	};

	// Fetch all calendar events
	const getAllCalendarEvents = useCallback(async () => {
		try {
			setAllEvents(filteredEventData);
			const allEvents = filteredEventData.map((item) => {
				return {
					id: item?.id,
					title: item?.title,
					allDay: item?.allDay,
					start: moment.utc(item?.startTime).format("YYYY-MM-DDTHH:mm:ss"),
					end: moment.utc(item?.endTime).format("YYYY-MM-DDTHH:mm:ss"),
				};
			});
			setEVENTS(allEvents);
		} catch (error) {
			console.log(error);
		}
	}, [filteredEventData]);

	useEffect(() => {
		getAllCalendarEvents();
		getTimeZoneInfo();
	}, [getAllCalendarEvents]);

	// Handle Create Event:
	const handleCreateEvent = () => {
		setShowModal(true);
		setModalContent(
			<CreateEvent
				title="New Event"
				onClose={() => setShowModal(false)}
				handleShowSuccessModal={handleShowSuccessModal}
			/>
		);
	};

	// Handle Show Success Modal:
	const handleShowSuccessModal = () => {
		setShowModal(true);
		setModalContent(
			<SuccessModal
				mode="fromCreate"
				title="Event Created"
				message="Event has been created successfully"
				onClose={() => setShowModal(false)}
				handleCreateEvent={handleCreateEvent}
			/>
		);
	};

	// Handle Date Selection
	const handleDateSelect = (selectInfo) => {
		setCalendarDate(selectInfo);
	};

	// Handle Modal Event | View Event:
	const handleEventClick = (clickInfo, allEvents) => {
		const eventId = clickInfo?.event?._def?.publicId;
		const event = allEvents;
		const thisEvent = event.find((event) => event?.id === eventId);

		handleViewEvent(thisEvent);
	};

	const handleEvents = () => {
		setCurrentEvents(currentEvents);
	};

	// Handle View Event:
	const handleViewEvent = (event) => {
		setShowModal(true);
		setModalContent(
			<ViewEvent
				event={event}
				title="View Event"
				onClose={() => setShowModal(false)}
				handleEditEvent={handleEditEvent}
			/>
		);
	};

	// Handle Edit Event:
	const handleEditEvent = (event) => {
		setShowModal(true);
		setModalContent(
			<EditEvent
				event={event}
				title="Edit Event"
				onClose={() => setShowModal(false)}
				handleShowSuccessModal={handleShowEditSuccessModal}
			/>
		);
	};

	// Handle Show Success Modal:
	const handleShowEditSuccessModal = () => {
		setShowModal(true);
		setModalContent(
			<SuccessModal
				mode="fromEdit"
				title="Event Saved"
				message="Event has been saved successfully"
				onClose={() => setShowModal(false)}
			/>
		);
	};

	// Render Event Content:
	const renderEventContent = (eventInfo) => {
		const dataStatus = filteredEventData.find(
			(item) => item?.id === eventInfo?.event?._def?.publicId
		);

		if (dataStatus) {
			return (
				<div className={`calendar_event_indicator ${dataStatus?.status}`}>
					<b>
						{moment.utc(dataStatus?.startTime).format("h:mm A")}{" "}
						<img src={videoCamera} alt="video camera" />
					</b>
					<i>{eventInfo?.event?.title}</i>
				</div>
			);
		}
	};

	// Side Effect | Calendar:
	useEffect(() => {
		if (location?.pathname === "/dashboard/calendar") {
			setView("Calendar");
		}

		document.title = "ED | Calendar";
	}, [location.pathname]);

	return (
		<>
			<section className="calendar">
				<div className="calendar_container">
					{/* Header */}
					<DateToggleControl
						view={view}
						handleCreateEvent={handleCreateEvent}
					/>

					{/* Content */}
					<div className="calendar_content">
						<FullCalendar
							plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
							headerToolbar={{
								left: "prev title next today",
								center: "timeGridDay,timeGridWeek,dayGridMonth",
								right: "",
							}}
							initialView="timeGridWeek"
							editable={true}
							selectable={true}
							selectMirror={true}
							dayMaxEventRows={1}
							weekends={true}
							events={EVENTS}
							select={handleDateSelect}
							eventContent={renderEventContent}
							eventClick={(clickInfo) => handleEventClick(clickInfo, allEvents)}
							eventsSet={handleEvents}
							dayHeaderContent={(args) => {
								const date = args.date;
								const day = date.toLocaleString("default", {
									weekday: "short",
								});
								const dayNumber = date.getDate();
								return (
									<div className="calendar_header">
										<span className="calendar_header_day">{day}</span>
										<span className="calendar_header_dayNumber">
											{dayNumber}
										</span>
									</div>
								);
							}}
							slotLabelFormat={{
								hour: "numeric",
								minute: "2-digit",
								omitZeroMinute: true,
								hour12: true,
							}}
						/>
						{currentTimeZone && (
							<div className="timezone-info">
								<span>{currentTimeZoneOffset}</span>
								<span>{currentTimeZone}</span>
							</div>
						)}
					</div>
				</div>
			</section>

			{/* Modal */}
			{showModal && <Modal modalContent={modalContent} />}

			<ToastContainer />
		</>
	);
}

export default Calendar;
